import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatNumber, formatTransactionValue } from '../../../../../shared/utils/numberFormatter';

const TransactionActivityChart = ({ data, timeRange, isLoading }) => {
  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          Loading transaction chart data...
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
          No transaction data available
        </div>
      </div>
    );
  }

  // Transform data for chart with proper date labels
  const chartData = data.map(item => {
    const date = new Date(item.bucket);
    let label;

    switch (timeRange) {
      case 'daily':
        // Show hour: "14:00", "15:00"
        label = date.toLocaleTimeString('en-US', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
        break;
      case 'weekly':
        // Show day: "Jan 1", "Jan 2"
        label = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
        break;
      case 'monthly':
        // Show day: "Jan 1", "Jan 2"
        label = date.toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric' 
        });
        break;
      case 'yearly':
        // Show month: "Jan 2024", "Feb 2024"
        label = date.toLocaleDateString('en-US', { 
          month: 'short', 
          year: 'numeric' 
        });
        break;
      default:
        label = date.toLocaleDateString('en-US', { 
          month: 'short', 
          year: 'numeric' 
        });
    }

    return {
      label,
      volume: item.volume || 0,
      value: item.value || 0
    };
  });

  // Calculate max values for Y-axis scaling
  const maxVolume = Math.max(...chartData.map(d => d.volume));
  const maxValue = Math.max(...chartData.map(d => d.value));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border shadow-lg" style={{ borderColor: styles.borderColor, fontFamily: 'Raleway, sans-serif' }}>
          <p className="font-medium mb-2" style={{ color: styles.textPrimary }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.dataKey === 'volume' 
                ? `Volume: ${formatNumber(entry.value, 0)}`
                : `Value: ${formatTransactionValue(entry.value)}`
              }
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom Y-axis tick formatter
  const formatVolumeAxis = (value) => formatNumber(value, 0);
  const formatValueAxis = (value) => formatTransactionValue(value);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart
        data={chartData}
        margin={{ top: 20, right: 80, left: 20, bottom: 20 }}
      >
        <CartesianGrid 
          strokeDasharray="3 3" 
          stroke="#E5E7EB"
          vertical={true}
          horizontal={true}
        />
        <XAxis 
          dataKey="label"
          tick={{ 
            fill: styles.textPrimary, 
            fontSize: 11,
            fontFamily: 'Raleway, sans-serif'
          }}
          axisLine={{ stroke: styles.borderColor }}
        />
        {/* Left Y-axis for Volume */}
        <YAxis 
          yAxisId="volume"
          orientation="left"
          tick={{ 
            fill: styles.textPrimary, 
            fontSize: 11,
            fontFamily: 'Raleway, sans-serif'
          }}
          axisLine={{ stroke: styles.borderColor }}
          tickFormatter={formatVolumeAxis}
          domain={[0, maxVolume * 1.1]}
        />
        {/* Right Y-axis for Value */}
        <YAxis 
          yAxisId="value"
          orientation="right"
          tick={{ 
            fill: styles.textPrimary, 
            fontSize: 11,
            fontFamily: 'Raleway, sans-serif'
          }}
          axisLine={{ stroke: styles.borderColor }}
          tickFormatter={formatValueAxis}
          domain={[0, maxValue * 1.1]}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend 
          wrapperStyle={{ fontFamily: 'Raleway, sans-serif', fontSize: '12px' }}
        />
        {/* Volume line (left Y-axis) */}
        <Line
          yAxisId="volume"
          type="monotone"
          dataKey="volume"
          stroke="#1d0c46"
          strokeWidth={2}
          name="Volume"
          isAnimationActive={true}
          dot={false}
        />
        {/* Value line (right Y-axis) */}
        <Line
          yAxisId="value"
          type="monotone"
          dataKey="value"
          stroke="#5d4d86"
          strokeWidth={2}
          name="Value"
          isAnimationActive={true}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

export default TransactionActivityChart;

